import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, ReactNode } from "react";
import { MailboxAccess, graphMailService, type MailFolder } from "../services/graphMailService";
import { canReadMailbox } from "../utils/mailboxAccess";
import { invalidateByPrefix } from "../utils/swrCache";
import { calendarService } from "../services/calendarService";
import { useAuth } from "./AuthContext";
import { useRealtime } from "./RealtimeContext";
import type { MailRealtimePayload } from "../services/realtimeService";
import { reportMailError } from "../services/mailError";
import { computeDefaultCalendars } from "../components/calendar/calendarDefault";

interface MailContextType {
    mailboxes: MailboxAccess[];
    /**
     * T-69: Kalender-Liste (GET calendar/mailboxes) — Grants, Kollegen nach
     * Outlook-Freigabe und Räume, mit `kalenderGruppe`. Quelle der
     * Kalenderauswahl und der geladenen Kalender. Bewusst getrennt von
     * `mailboxes` (Mail-Liste, Schreibrechte): ein Kollegen- oder Raumkalender
     * ohne Grant steht nur hier.
     */
    calendarMailboxes: MailboxAccess[];
    mailboxesLoading: boolean;
    /** Default write target (where new events are created). */
    currentMailbox: string | null;
    setCurrentMailbox: (mailbox: string | null) => void;
    /**
     * SP3 (Review 2026-07-07): Adresse des EIGENEN Postfachs (Backend-Flag
     * `isOwn`, Fallback Login-E-Mail) — Sende-Default der Termin-Anlage, damit
     * ein Termin nicht versehentlich vom gerade im Mail-Bereich aktiven
     * Fremd-Postfach (z. B. cbb@) ausgeht. null, solange unbekannt.
     */
    ownMailbox: string | null;
    /** Calendars currently overlaid in the calendar view (addresses). */
    selectedCalendars: string[];
    toggleCalendar: (address: string) => void;
    setSelectedCalendars: (addresses: string[]) => void;
    /** Stable colour for a calendar/mailbox address (used for colour-coding). */
    calendarColor: (address: string) => string;
    refreshMailboxes: () => Promise<void>;
    // ── T3: Ordnerbaum-Datenschicht (eager je lesbarem Postfach) ──
    /** Vollständiger Ordnerbaum je Postfach-Adresse (eager geladen). */
    folderTrees: Record<string, MailFolder[]>;
    folderTreesLoading: boolean;
    /** Ohne Argument: alle Bäume; mit Postfach-Adresse: nur dieses (B6). */
    refreshFolders: (onlyMailbox?: string) => Promise<void>;
    /** Summe der ungelesenen Mails (über alle Ordner) je Postfach-Adresse. */
    unreadByMailbox: Record<string, number>;
    /** Aktuell gewählter Ordner im aktiven Postfach (null = Standard/Posteingang). */
    activeFolder: { id: string; displayName: string } | null;
    setActiveFolder: (folder: { id: string; displayName: string } | null) => void;
    /**
     * SP4: Zentraler Mail-Such-Text — geteilt zwischen der MailView-Liste und der
     * Topbar-Suche bzw. dem Handy-Kopf (`MailMobilKopf`). Bewusst
     * EINE Quelle der Wahrheit: zwei konkurrierende `searchQuery`-States (Leiste
     * vs. Liste) wären ein Race (Risiko §9). Desktop/Tablet speisen ihn über die
     * Topbar-Suche, das Phone über die Suche im Kopf der Liste.
     */
    searchQuery: string;
    setSearchQuery: (q: string) => void;
    /**
     * T1 (PR5): Zähler, der bei einem Echtzeit-`mail`-Frame für das AKTUELLE
     * Postfach inkrementiert — Konsumenten (MailView) hängen ihn an ihre
     * Fetch-Effekt-Dependencies, um die Liste live neu zu laden.
     */
    mailRefreshTick: number;
}

/** Ungelesene des **echten Posteingangs** (Inbox-Ordner selbst) — ohne Unterordner,
 *  Junk, Gelöscht, Archiv. Die Inbox markiert das Backend zuverlässig per
 *  `wellKnownName='inbox'` (Fallback: displayName Posteingang/Inbox). */
const findInbox = (nodes: MailFolder[]): MailFolder | null => {
    for (const n of nodes) {
        if (n.wellKnownName === "inbox") return n;
        const c = findInbox(n.children ?? []);
        if (c) return c;
    }
    return null;
};
const inboxUnread = (nodes: MailFolder[]): number => {
    const inbox =
        findInbox(nodes) ??
        nodes.find((n) => /^(posteingang|inbox)$/i.test((n.displayName || "").trim())) ??
        null;
    return inbox?.unreadItemCount ?? 0;
};

const MailContext = createContext<MailContextType | undefined>(undefined);

const CURRENT_MAILBOX_KEY = "mail.currentMailbox";
const SELECTED_CALENDARS_KEY = "calendar.selectedCalendars";

/**
 * Fixed, high-contrast palette so calendars keep the same colour across
 * sessions and views. Assignment is by a stable hash of the address, so a
 * given mailbox always maps to the same swatch.
 */
const CALENDAR_PALETTE = [
    "#0078d4", // blue
    "#107c10", // green
    "#d83b01", // orange
    "#5c2d91", // purple
    "#008272", // teal
    "#a4262c", // red
    "#8764b8", // violet
    "#c19c00", // gold
    "#005b70", // dark teal
    "#b146c2", // magenta
];

const colorForAddress = (address: string): string => {
    const key = (address || "").toLowerCase();
    let hash = 0;
    for (let i = 0; i < key.length; i++) {
        hash = (key.charCodeAt(i) + ((hash << 5) - hash)) | 0;
    }
    return CALENDAR_PALETTE[Math.abs(hash) % CALENDAR_PALETTE.length];
};

const readStored = (key: string): string[] => {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === "string") : [];
    } catch {
        return [];
    }
};

export const MailProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { isAuthenticated, user } = useAuth();
    const [mailboxes, setMailboxes] = useState<MailboxAccess[]>([]);
    const [mailboxesLoading, setMailboxesLoading] = useState(false);
    const [currentMailbox, setCurrentMailboxState] = useState<string | null>(() => {
        try {
            return localStorage.getItem(CURRENT_MAILBOX_KEY);
        } catch {
            return null;
        }
    });
    const [selectedCalendars, setSelectedCalendarsState] = useState<string[]>(() =>
        readStored(SELECTED_CALENDARS_KEY),
    );
    // T3: Ordnerbäume je Postfach (eager) + aktive Ordner-Auswahl.
    const [folderTrees, setFolderTrees] = useState<Record<string, MailFolder[]>>({});
    const [folderTreesLoading, setFolderTreesLoading] = useState(false);
    const [activeFolder, setActiveFolderState] = useState<{ id: string; displayName: string } | null>(null);
    // SP4: geteilter Mail-Such-Text (Liste ↔ mobile Bottom-Mail-Leiste).
    const [searchQuery, setSearchQuery] = useState("");
    // T1 (PR5): Live-Refresh-Signal für die offene Liste.
    const [mailRefreshTick, setMailRefreshTick] = useState(0);
    const { subscribe: subscribeRealtime } = useRealtime();

    /**
     * SP7: separate Kalender-Liste (GET calendar/mailboxes, calendarAccess>=READ)
     * NUR für Default-Set + Auswahl-Validierung. `mailboxes` (Mail-Quelle) bleibt
     * für andere Mail-Consumer unangetastet (Risiko §11).
     */
    const calendarMailboxesRef = useRef<MailboxAccess[]>([]);
    const [calendarMailboxes, setCalendarMailboxes] = useState<MailboxAccess[]>([]);
    /** Ob die Server-Auswahl bereits hydriert wurde (verhindert Re-Hydration). */
    const hydratedFromServerRef = useRef(false);
    // B4: einmaliger, nicht stapelnder Retry-Timer für refreshMailboxes.
    const mailboxRetryRef = useRef<number | null>(null);
    const refreshMailboxesRef = useRef<(() => Promise<void>) | null>(null);

    /**
     * SP7: Auswahl serverseitig persistieren (best-effort, fire-and-forget). Der
     * Server filtert auf lesbare Kalender und ist single source of truth für
     * „geräteübergreifend"; localStorage bleibt Offline-/Schreib-Fallback.
     */
    const persistSelectedToServer = useCallback((addresses: string[]) => {
        if (!hydratedFromServerRef.current) return; // erst nach erfolgreicher Hydration
        void calendarService.saveCalendarPreferences(addresses).catch((error) => {
            reportMailError({
                area: "mail-calendar",
                action: "save-calendar",
                error,
                fallbackMessage: "Die Kalenderauswahl konnte nicht gespeichert werden.",
            });
            // localStorage bleibt Fallback (kein Sync-Konfliktschutz nötig)
        });
    }, []);

    const persistSelected = useCallback(
        (addresses: string[]) => {
            try {
                localStorage.setItem(SELECTED_CALENDARS_KEY, JSON.stringify(addresses));
            } catch {
                // ignore
            }
            persistSelectedToServer(addresses);
        },
        [persistSelectedToServer],
    );

    const setSelectedCalendars = useCallback(
        (addresses: string[]) => {
            const unique = Array.from(new Set(addresses));
            setSelectedCalendarsState(unique);
            persistSelected(unique);
        },
        [persistSelected],
    );

    const toggleCalendar = useCallback(
        (address: string) => {
            setSelectedCalendarsState((prev) => {
                const next = prev.includes(address)
                    ? prev.filter((a) => a !== address)
                    : [...prev, address];
                persistSelected(next);
                return next;
            });
        },
        [persistSelected],
    );

    const setCurrentMailbox = useCallback((mailbox: string | null) => {
        setCurrentMailboxState(mailbox);
        try {
            if (mailbox) localStorage.setItem(CURRENT_MAILBOX_KEY, mailbox);
            else localStorage.removeItem(CURRENT_MAILBOX_KEY);
        } catch {
            // ignore
        }
    }, []);

    const refreshMailboxes = useCallback(async () => {
        if (!isAuthenticated) {
            setMailboxes([]);
            calendarMailboxesRef.current = [];
            setCalendarMailboxes([]);
            hydratedFromServerRef.current = false; // bei Re-Login neu hydrieren
            return;
        }
        setMailboxesLoading(true);
        try {
            // SP7: Mail- UND Kalender-Liste laden. Mail-Liste bleibt Quelle für
            // `mailboxes`/`currentMailbox` (Write-Target). Kalender-Liste (>=READ)
            // ist die einzige Quelle für Default-Set + Auswahl-Validierung.
            const [list, calendarListOrNull] = await Promise.all([
                graphMailService.listMailboxes(),
                calendarService.listMailboxes().catch((error) => {
                    reportMailError({
                        area: "mail-calendar",
                        action: "load-calendar",
                        error,
                        fallbackMessage: "Die Kalenderpostfächer konnten nicht geladen werden.",
                    });
                    return null;
                }),
            ]);
            setMailboxes(list);
            // T-69: Ein Ladefehler darf die zuletzt bekannte Kalender-Liste nicht
            // leeren — sonst verschwänden alle Kalender aus der Ansicht.
            const calendarList = calendarListOrNull ?? calendarMailboxesRef.current;
            calendarMailboxesRef.current = calendarList;
            setCalendarMailboxes(calendarList);

            // F3: Das EIGENE Postfach zuverlässig bestimmen — bevorzugt über das
            // Backend-Flag isOwn (Adresse == Session-E-Mail), sonst FE-Abgleich
            // gegen die Login-E-Mail. Die Mail-Liste enthält per OR-Filter auch
            // kalender-only Postfächer (mailAccess=NONE); für das Sende-Ziel
            // currentMailbox darf daher nur ein mail-nutzbares Postfach dienen.
            const ownEmail = user?.email?.toLowerCase() ?? null;
            const ownEntry =
                list.find((m) => m.isOwn) ??
                (ownEmail
                    ? list.find((m) => m.mailboxAddress.toLowerCase() === ownEmail)
                    : undefined);
            const ownSendable =
                ownEntry && ownEntry.mailAccess !== "NONE" ? ownEntry.mailboxAddress : null;
            // Für die Kalender-Default-Ableitung reicht die reine Eigen-Adresse;
            // computeDefaultCalendars prüft calendarAccess selbst gegen die
            // Kalender-Liste (kalender-only=NONE erscheint dort korrekt nicht).
            const ownAddress =
                ownEntry?.mailboxAddress ??
                (ownEmail
                    ? calendarList.find((m) => m.mailboxAddress.toLowerCase() === ownEmail)
                          ?.mailboxAddress ?? null
                    : null);
            setCurrentMailboxState((prev) => {
                // Laufende Wahl bleibt bestehen, solange sie in der Liste ist.
                if (prev && list.some((m) => m.mailboxAddress === prev)) return prev;
                // Initial-Default: eigenes (sende-fähiges) Postfach, sonst list[0].
                const fallback = ownSendable ?? list[0]?.mailboxAddress ?? null;
                try {
                    if (fallback) localStorage.setItem(CURRENT_MAILBOX_KEY, fallback);
                    else localStorage.removeItem(CURRENT_MAILBOX_KEY);
                } catch {
                    // ignore
                }
                return fallback;
            });

            // SP7: Server-Auswahl einmalig hydrieren. Server gewinnt, wenn nicht
            // leer (geräteübergreifend). Best-effort — bei Fehler bleibt
            // localStorage maßgeblich.
            let serverSelection: string[] | null = null;
            if (!hydratedFromServerRef.current) {
                try {
                    const pref = await calendarService.getCalendarPreferences();
                    serverSelection = Array.isArray(pref?.selectedCalendars)
                        ? pref.selectedCalendars
                        : [];
                } catch (error) {
                    reportMailError({
                        area: "mail-calendar",
                        action: "load-calendar",
                        error,
                        fallbackMessage: "Die Kalenderauswahl konnte nicht geladen werden.",
                    });
                    serverSelection = null; // Endpoint nicht erreichbar → localStorage-Fallback
                }
            }

            // Auswahl gegen die KALENDER-Liste validieren (nicht die Mail-Liste):
            // ein nur mailbarer Kalender darf nicht selektiert bleiben (sonst 403).
            const validCalendars = new Set(
                calendarList
                    .filter((m) => m.calendarAccess !== "NONE")
                    .map((m) => m.mailboxAddress),
            );

            setSelectedCalendarsState((prev) => {
                // 1) Server-Auswahl (falls hydriert und nicht leer) gewinnt.
                if (serverSelection && serverSelection.length > 0) {
                    const fromServer = serverSelection.filter((a) => validCalendars.has(a));
                    const next = fromServer.length > 0 ? fromServer : prev;
                    try {
                        localStorage.setItem(SELECTED_CALENDARS_KEY, JSON.stringify(next));
                    } catch {
                        // ignore
                    }
                    return next;
                }
                // 2) Bestehende lokale Auswahl gegen die Kalender-Liste filtern.
                const filtered = prev.filter((a) => validCalendars.has(a));
                if (filtered.length > 0) {
                    if (filtered.length !== prev.length) {
                        try {
                            localStorage.setItem(SELECTED_CALENDARS_KEY, JSON.stringify(filtered));
                        } catch {
                            // ignore
                        }
                        // Server nachziehen (best-effort), wenn schon hydriert.
                        if (hydratedFromServerRef.current) {
                            void calendarService.saveCalendarPreferences(filtered).catch((error) => {
                                reportMailError({
                                    area: "mail-calendar",
                                    action: "save-calendar",
                                    error,
                                    fallbackMessage: "Die Kalenderauswahl konnte nicht gespeichert werden.",
                                });
                            });
                        }
                    }
                    return filtered;
                }
                // 3) Erst-Login / leerer Stand → Default-Set (eigener + alle zugewiesenen).
                const seed = computeDefaultCalendars(calendarList, ownAddress);
                try {
                    localStorage.setItem(SELECTED_CALENDARS_KEY, JSON.stringify(seed));
                } catch {
                    // ignore
                }
                // Seed serverseitig persistieren, damit er geräteübergreifend gilt.
                void calendarService.saveCalendarPreferences(seed).catch((error) => {
                    reportMailError({
                        area: "mail-calendar",
                        action: "save-calendar",
                        error,
                        fallbackMessage: "Die Kalenderauswahl konnte nicht gespeichert werden.",
                    });
                });
                return seed;
            });

            // Hydration ist nach dem ersten erfolgreichen Lauf abgeschlossen — ab
            // jetzt schreiben Toggles/Set auch zum Server.
            hydratedFromServerRef.current = true;
        } catch (error) {
            // B4: transienter Fehler darf den Picker NICHT leeren — die zuletzt
            // bekannte Liste bleibt stehen; ein Retry heilt (einmalig, 10 s).
            reportMailError({
                area: "mail",
                action: "load-mailboxes",
                error,
                fallbackMessage: "Die Postfächer konnten nicht geladen werden.",
            });
            if (!mailboxRetryRef.current) {
                mailboxRetryRef.current = window.setTimeout(() => {
                    mailboxRetryRef.current = null;
                    void refreshMailboxesRef.current?.();
                }, 10_000);
            }
        } finally {
            setMailboxesLoading(false);
        }
    }, [isAuthenticated]);
    // Selbstreferenz für den verzögerten Retry (Callback ist zum Timer-Zeitpunkt
    // evtl. neu erzeugt — die Ref zeigt immer auf die aktuelle Fassung).
    refreshMailboxesRef.current = refreshMailboxes;

    useEffect(() => {
        refreshMailboxes();
    }, [refreshMailboxes]);

    const setActiveFolder = useCallback(
        (folder: { id: string; displayName: string } | null) => setActiveFolderState(folder),
        [],
    );

    /**
     * T3: Ordnerbäume EAGER je lesbarem Postfach laden (ein /mail/folders-Call
     * pro Postfach) → die Ungelesen-Zähler stehen app-weit ohne Klick bereit.
     *
     * B4: Ein Fehler lässt den ZULETZT bekannten Baum des Postfachs stehen
     * (vorher: stilles `[]` ⇒ Badge fiel auf 0, Postfach wirkte „leer").
     * B6: `onlyMailbox` erlaubt den GEZIELTEN Refresh eines Postfachs — der
     * Realtime-Frame-Handler lädt damit nicht mehr bei jedem Frame ALLE Bäume
     * neu (Stampede → Graph-Throttling).
     */
    const refreshFolders = useCallback(async (onlyMailbox?: string) => {
        if (!isAuthenticated) {
            setFolderTrees({});
            return;
        }
        // B1: Rang-Semantik statt exaktem String-Match — SEND beinhaltet Lesen,
        // sonst fehlen SEND-Postfächern (cbb@/schaden@) Ordnerbaum + Badge.
        const readable = mailboxes.filter(canReadMailbox);
        if (!readable.length) {
            setFolderTrees({});
            return;
        }
        const targets = onlyMailbox
            ? readable.filter((m) => m.mailboxAddress.toLowerCase() === onlyMailbox.toLowerCase())
            : readable;
        if (!targets.length) return;
        if (!onlyMailbox) setFolderTreesLoading(true);
        try {
            const entries = await Promise.all(
                targets.map(async (m) => {
                    try {
                        return [m.mailboxAddress, await graphMailService.fetchFolderTree(m.mailboxAddress)] as const;
                    } catch (error) {
                        reportMailError({
                            area: "mail",
                            action: "load-folders",
                            error,
                            fallbackMessage: "Die Mail-Ordner konnten nicht geladen werden.",
                        });
                        // B4: Fehler → letzten bekannten Baum behalten (Marker null).
                        return [m.mailboxAddress, null] as const;
                    }
                }),
            );
            setFolderTrees((prev) => {
                const next = onlyMailbox ? { ...prev } : { ...prev };
                for (const [mb, tree] of entries) {
                    if (tree !== null) next[mb] = tree;
                    else if (!(mb in next)) next[mb] = [];
                }
                // Voll-Refresh: entfernte Postfächer aufräumen.
                if (!onlyMailbox) {
                    const wanted = new Set(readable.map((m) => m.mailboxAddress));
                    for (const key of Object.keys(next)) if (!wanted.has(key)) delete next[key];
                }
                return next;
            });
        } finally {
            if (!onlyMailbox) setFolderTreesLoading(false);
        }
    }, [isAuthenticated, mailboxes]);

    // Ordnerbäume laden, sobald die Postfach-Liste steht (oder sich ändert).
    useEffect(() => {
        void refreshFolders();
    }, [refreshFolders]);

    // T1 (PR5): aktuelles Postfach in einer Ref spiegeln (für den Frame-Handler,
    // damit das Abo nicht bei jedem Postfach-Wechsel neu aufgebaut wird).
    const currentMailboxRef = useRef<string | null>(currentMailbox);
    useEffect(() => {
        currentMailboxRef.current = currentMailbox;
    }, [currentMailbox]);

    // T1 (PR5): Echtzeit-`mail`-Frames konsumieren — Caches des betroffenen
    // Postfachs verwerfen, Badges (Ordnerbäume) auffrischen, und die offene Liste
    // nur neu laden, wenn das AKTUELLE Postfach betroffen ist.
    useEffect(() => {
        if (!isAuthenticated) return;
        const unsub = subscribeRealtime("mail", (evt) => {
            const p = evt.payload as MailRealtimePayload | undefined;
            if (!p?.mailbox) return;
            graphMailService.invalidateMailbox(p.mailbox);
            // B6: nur den Baum des BETROFFENEN Postfachs neu laden — vorher lud
            // jeder Frame alle Bäume aller Postfächer (Stampede).
            void refreshFolders(p.mailbox);
            if (p.mailbox === currentMailboxRef.current) {
                setMailRefreshTick((t) => t + 1);
            }
        });
        return unsub;
    }, [isAuthenticated, subscribeRealtime, refreshFolders]);

    // B8 — SSE-Reconnect-Heilung: während eines Verbindungsabrisses sind Frames
    // unwiederbringlich verloren (In-Memory-Bus, kein Replay). Beim Wiederaufbau
    // einmal ALLE Mail-Caches verwerfen + Bäume/Liste neu laden, damit Badges
    // und offene Liste den verpassten Stand aufholen.
    useEffect(() => {
        if (!isAuthenticated) return;
        const unsub = subscribeRealtime("reconnect", () => {
            invalidateByPrefix("mail.");
            void refreshFolders();
            setMailRefreshTick((t) => t + 1);
            // SP3: Nicht bis zum nächsten Server-Delta-Poll (Cron) warten — den Delta
            // des AKTUELLEN Postfachs SOFORT anstoßen, damit während des Ausfalls
            // eingegangene Mails ohne Verzögerung erscheinen. Fire-and-forget; ein
            // Fehler darf die Heilung (refreshFolders/Tick oben) nicht abbrechen.
            // Guard: nur ein gesetztes, echtes Postfach (Adresse mit „@") — es gibt
            // kein Demo-/Platzhalter-Postfach, das einen Server-Sync auslösen dürfte.
            const mb = currentMailboxRef.current;
            if (mb && mb.includes("@")) {
                void graphMailService.syncMailbox(mb).catch((error) => {
                    reportMailError({
                        area: "mail",
                        action: "sync",
                        error,
                        fallbackMessage: "Das Postfach konnte nicht synchronisiert werden.",
                    });
                    /* best effort — Bäume/Liste heilen ohnehin über refreshFolders/Tick */
                });
            }
        });
        return unsub;
    }, [isAuthenticated, subscribeRealtime, refreshFolders]);

    // Hinweis (PR4): KEIN Auto-Reset von activeFolder beim Postfach-Wechsel — die
    // Ordner-Auswahl wird im Mail-Menü explizit gesetzt (Postfach + Ordner atomar,
    // inkl. selectFolder(mb, null) für „Posteingang"). Ein Reset würde einen
    // Cross-Postfach-Ordnerklick sofort wieder auf null ziehen.

    const unreadByMailbox = useMemo<Record<string, number>>(() => {
        const out: Record<string, number> = {};
        for (const [mb, tree] of Object.entries(folderTrees)) out[mb] = inboxUnread(tree);
        return out;
    }, [folderTrees]);

    const calendarColor = useCallback((address: string) => colorForAddress(address), []);

    // SP3 (Review 2026-07-07): Eigenes Postfach als Sende-Default der Termin-
    // Anlage — bevorzugt das Backend-Flag `isOwn` (Adresse == Session-E-Mail),
    // sonst Abgleich gegen die Login-E-Mail. `mailboxes` enthält per OR-Filter
    // auch kalender-only Postfächer; das calendarAccess-Gate macht
    // resolveDefaultCalendarMailbox am Verbrauchsort, hier daher ohne Gate.
    const ownMailbox = useMemo<string | null>(() => {
        const ownEmail = user?.email?.toLowerCase() ?? null;
        return (
            mailboxes.find((m) => m.isOwn)?.mailboxAddress ??
            (ownEmail
                ? mailboxes.find((m) => m.mailboxAddress.toLowerCase() === ownEmail)
                      ?.mailboxAddress ?? null
                : null)
        );
    }, [mailboxes, user?.email]);

    const value = useMemo<MailContextType>(
        () => ({
            mailboxes,
            calendarMailboxes,
            mailboxesLoading,
            currentMailbox,
            setCurrentMailbox,
            ownMailbox,
            selectedCalendars,
            toggleCalendar,
            setSelectedCalendars,
            calendarColor,
            refreshMailboxes,
            folderTrees,
            folderTreesLoading,
            refreshFolders,
            unreadByMailbox,
            activeFolder,
            setActiveFolder,
            searchQuery,
            setSearchQuery,
            mailRefreshTick,
        }),
        [
            mailboxes,
            calendarMailboxes,
            mailboxesLoading,
            currentMailbox,
            setCurrentMailbox,
            ownMailbox,
            selectedCalendars,
            toggleCalendar,
            setSelectedCalendars,
            calendarColor,
            refreshMailboxes,
            folderTrees,
            folderTreesLoading,
            refreshFolders,
            unreadByMailbox,
            activeFolder,
            setActiveFolder,
            searchQuery,
            setSearchQuery,
            mailRefreshTick,
        ],
    );

    return <MailContext.Provider value={value}>{children}</MailContext.Provider>;
};

/** Wie {@link useMailContext}, aber ohne Provider `null` statt Fehler — für Bausteine, die auch außerhalb laufen. */
export const useMailContextOptional = () => useContext(MailContext) ?? null;

export const useMailContext = () => {
    const context = useContext(MailContext);
    if (context === undefined) {
        throw new Error("useMailContext must be used within a MailProvider");
    }
    return context;
};
